import { LogMessage } from '@/shared/types/analytics';
import { ModelStrategyStep } from '@/shared/types/modelStrategy';
import {
  AgentMessage,
  BrainMessage,
  BrainMessage as LegacyBrainMessage,
  DebugStepType,
  Message,
  NodeType,
  Response,
  ResponseMessage,
  SynthesisResponseCode,
  TryItCollection,
  UserMessage,
  UserMessage as LegacyUserMessage,
} from '@/shared/types/tryIt';
import { isSameMinute, parseTimestamp } from '@/util/dateUtils';

import {
  ConversationTurn,
  ConversationTurnCategory,
  ConversationTurnCategoryKind,
} from '../types/conversation';

// ─────────────────────────────────────────────────────────────────────────────
// Category i18n label map
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORY_LABEL_KEY_MAP: Record<ConversationTurnCategoryKind, string> = {
  workflow: 'try_it.category.workflow',
  knowledge_base: 'try_it.category.knowledge_base',
  guidelines_llm: 'try_it.category.guidelines_llm',
  mcp: 'try_it.category.mcp',
  broadcast: 'try_it.category.broadcast',
  campaign: 'try_it.category.campaign',
  agent: 'try_it.category.agent',
  voice_campaign: 'try_it.category.voice_campaign',
  unknown: 'try_it.category.unknown',
  no_details: 'try_it.category.no_details',
};

const buildCategory = (kind: ConversationTurnCategoryKind): ConversationTurnCategory => ({
  kind,
  label: CATEGORY_LABEL_KEY_MAP[kind],
});

// Real KB answer = doc-linked external URLs or fragments; a "Guidelines" URL is not a KB.
export const isCollectionMessage = (collection?: TryItCollection): boolean => {
  if (collection?.external_urls?.some((url) => url.document_id)) {
    return true;
  }
  return Boolean(
    collection?.fragments?.length &&
    (collection.response_code === SynthesisResponseCode.OK || collection.response_code == null)
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Test-mode path: brain Response → ConversationTurn
// ─────────────────────────────────────────────────────────────────────────────

const inferCategoryKindFromResponse = (response: Response): ConversationTurnCategoryKind => {
  const debug = response.output.debug;
  if (debug?.nodes_stack && debug.nodes_stack.length > 0) {
    return 'workflow';
  }
  if (isCollectionMessage(debug?.collection)) {
    return 'knowledge_base';
  }
  if (debug?.standalone_question || response.output.planner_intents?.length) {
    return 'guidelines_llm';
  }
  if (debug?.tools_invoked?.length) {
    return 'mcp';
  }
  return 'unknown';
};

type BuildTestTurnArgs = {
  response: Response;
  requestId: string;
  /** Optional override for the canonical turn id (defaults to the last node in nodes_stack, then request_id). */
  turnId?: string;
  /** Wall-clock timestamp for the turn — defaults to now. */
  at?: string;
  languageModel?: ModelStrategyStep;
};

/**
 * Optimistic placeholder turn shown the instant the user sends a test message —
 * the request text with no responses yet. Replaced by the real turn(s) on
 * `sendSucceeded`, which drops trailing `pending` turns first.
 */
export const buildPendingTurn = ({
  text,
  at = new Date().toISOString(),
}: {
  text: string;
  at?: string;
}): ConversationTurn => ({
  id: 'pending',
  at,
  request: { text, isAudio: false, attachments: [] },
  category: buildCategory('no_details'),
  responses: [],
  metadata: { isError: false, isInterrupted: false, isAuthentication: false },
  pending: true,
});

/**
 * Builds a single `ConversationTurn` directly from a brain response. Pure —
 * tested in isolation and reused by `useTestSend` (per-response dispatch).
 */
export const buildTestTurn = ({
  response,
  requestId,
  turnId,
  at = new Date().toISOString(),
  languageModel,
}: BuildTestTurnArgs): ConversationTurn => {
  const debug = response.output.debug;
  const lastNode = debug?.nodes_stack?.[debug.nodes_stack.length - 1];
  const kind = inferCategoryKindFromResponse(response);
  const id = turnId ?? lastNode?.node_id ?? requestId;

  return {
    id,
    at,
    request: {
      text: response.input.text,
      isAudio: false,
      attachments: [],
      intents: response.output.intents,
      entities: response.output.entities,
    },
    category: buildCategory(kind),
    responses: response.output.responses ?? [],
    metadata: {
      intent: response.output.intents?.[0],
      triggeredNode:
        lastNode && lastNode.name ? { id: lastNode.node_id, name: lastNode.name } : undefined,
      knowledgeBase: debug?.collection?.collection_id
        ? {
            id: debug.collection.collection_id,
            name: lastNode?.name ?? '',
            contents: debug.collection.fragments ?? [],
          }
        : undefined,
      nodesStack: debug?.nodes_stack,
      context: response.context,
      steps: debug?.steps,
      // Built even with no collection payload — the synthesis codes live at the debug root.
      collection: debug && {
        ...debug.collection,
        request_code: debug.collection?.request_code ?? debug.request_code,
        response_code: debug.collection?.response_code ?? debug.response_code,
      },
      languageModel,
      toolCalls: debug?.tools_invoked,
      plannerIntents: response.output.planner_intents,
      standaloneQuestion: debug?.standalone_question,
      authentication: debug?.authentication,
      isError: false,
      isInterrupted: false,
      isAuthentication: Boolean(debug?.authentication),
    },
    corrections: response.output.corrections,
    requestId,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Replay path: LogMessage[] → NodeType[] → ConversationTurn[]
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Replay-path helper: collapses a flat `LogMessage[]` stream from the analytics
 * GraphQL into the legacy `NodeType[]` shape.
 *
 * This is a **verbatim move** from `modules/tryIt/utils/helper.ts`. Nothing
 * about the parsing logic is new — keeping the proven event-coalescing logic
 * in place lets phase 1 ship without risking a regression.
 *
 * TODO(phase-2): rewrite directly to `LogMessage[] → ConversationTurn[]`, drop
 * the `NodeType` intermediate, and delete `nodeToLegacyTurn` + `turnToLegacyNode`.
 */
type ReceivedPayload = {
  text?: string;
  type?: string;
  attachments?: unknown[];
  reminder?: {
    minutes?: number;
    reminder_seconds?: number;
    reminder_id?: string;
    trigger_node_id?: string;
  };
};

const hasUserContent = (payload?: ReceivedPayload) =>
  Boolean(
    payload?.text?.trim() ||
    payload?.attachments?.length ||
    payload?.reminder ||
    payload?.type === 'audio' ||
    payload?.type === 'whatsapp_flow'
  );

const buildUserMessage = (requestId: string, payload: ReceivedPayload) => ({
  intents: null,
  request_id: requestId,
  text: payload.text,
  type: 'user',
  isAudio: payload.type === 'audio',
  attachments: payload.attachments,
  isFlowResponse: payload.type === 'whatsapp_flow',
  reminder: payload.reminder
    ? {
        minutes: payload.reminder.minutes,
        reminder_seconds: payload.reminder.reminder_seconds,
        reminder_id: payload.reminder.reminder_id,
        trigger_node_id: payload.reminder.trigger_node_id,
      }
    : undefined,
});

// Same-minute grouping stops at a channel switch, so the replay can mark where it happened.
const isSameChannel = (a: LogMessage, b: LogMessage) =>
  (a.integration_id ?? a.channel) === (b.integration_id ?? b.channel);

export const toTimestamp = (time?: string): number =>
  parseTimestamp(time ?? '')?.getTime() ?? Number.POSITIVE_INFINITY;

const sortByTime = (messages: LogMessage[]): LogMessage[] =>
  [...messages].sort((a, b) => toTimestamp(a.time) - toTimestamp(b.time));

export const calculateSessionNodes = (payload?: LogMessage[]): NodeType[] => {
  if (!payload) {
    return [];
  }
  const messages = sortByTime(payload);

  const newNodes: NodeType[] = messages.reduce((acc, message, idx) => {
    const time = message.time;
    const channel = message.channel;
    const integration_id = message.integration_id ?? undefined;

    switch (message.event) {
      case 'message:received': {
        const resp = messages.find(
          (m, index) =>
            m.request_id === message.request_id && m.event === 'message:brain_send' && index > idx
        ) as Extract<LogMessage, { event: 'message:brain_send' }> | null;
        // user -> live agent
        if (!resp) {
          for (let j = 0; j < acc.length; j++) {
            if (acc[j].messages.find((m) => m.request_id === message.request_id)) {
              return acc;
            }
          }

          const userMessages = hasUserContent(message.message)
            ? [buildUserMessage(message.request_id, message.message)]
            : [];
          for (let i = idx + 1; i < messages.length; i++) {
            const nextMessage = messages[i];
            if (
              nextMessage.event === 'message:received' &&
              isSameMinute(time, messages[i].time) &&
              isSameChannel(message, nextMessage)
            ) {
              if (hasUserContent(nextMessage.message)) {
                userMessages.push(buildUserMessage(nextMessage.request_id, nextMessage.message));
              }
            } else {
              break;
            }
          }

          if (userMessages.length === 0) {
            return acc;
          }

          return [
            ...acc,
            {
              context: message.context,
              tags: message.tags,
              messages: userMessages,
              author_type: 'user',
              time,
              channel,
              integration_id,
            },
          ];
        }
        // user <-> brain
        const nodes_stack = resp?.message?.debug?.nodes_stack ?? [];
        const standalone_question = resp?.message?.debug?.standalone_question;
        const receivedPayload = { ...message.message, reminder: resp.message?.reminder };
        return [
          ...acc,
          {
            id: nodes_stack[nodes_stack.length - 1]?.node_id,
            context: resp.context,
            tags: resp.tags,
            name: nodes_stack[nodes_stack.length - 1]?.name,
            debugLogs: resp.message.debug?.logs,
            steps: resp.message.debug?.steps,
            time,
            channel,
            integration_id,
            request_id: resp.request_id,
            nodes_stack,
            standalone_question,
            collection: {
              collection_id: resp?.collection_id,
              request_code: resp?.collection_request_code,
              response_code: resp?.collection_response_code,
              fragments: resp?.collection_sources,
              external_urls: resp?.message.debug?.external_urls,
            },
            corrections: resp.corrections,
            reaction: resp.reaction,
            planner_intents: resp?.message?.planner_intents,
            authentication: resp?.message?.debug?.authentication,
            tool_calls: resp?.message?.debug?.tools_invoked?.length
              ? { tool_invocations: resp.message.debug.tools_invoked }
              : undefined,
            messages: [
              ...(hasUserContent(receivedPayload)
                ? [
                    {
                      ...buildUserMessage(message.request_id, receivedPayload),
                      entities: resp.message.entities,
                      intents: resp.message.intents,
                    },
                  ]
                : []),
              { responses: resp.message.responses, type: 'brain' },
            ],
            author_type: 'brain',
            author_id: resp?.brain_parent_id,
          },
        ];
      }
      case 'message:send': {
        for (let j = 0; j < acc.length; j++) {
          if (acc[j].messages.find((m) => m.request_id === message.request_id)) {
            return acc;
          }
        }
        const index = messages.findIndex((m) => m.request_id === message.request_id);
        const agentMessages = [
          {
            request_id: message.request_id,
            text: message.message.text,
            type: 'agent',
            template: message.message?.body?.template,
            body: message.message?.body,
          },
        ];
        for (let i = index + 1; i < messages.length; i++) {
          const nextMessage = messages[i];
          if (
            nextMessage.event === 'message:send' &&
            isSameMinute(time, nextMessage.time) &&
            nextMessage.agent_id === message.agent_id &&
            isSameChannel(message, nextMessage)
          ) {
            agentMessages.push({
              request_id: messages[i].request_id,
              text: nextMessage.message.text,
              type: 'agent',
              template: nextMessage.message?.body?.template,
              body: nextMessage.message?.body,
            });
          } else {
            break;
          }
        }
        return [
          ...acc,
          {
            time,
            channel,
            integration_id,
            context: message.context,
            tags: message.tags,
            messages: agentMessages,
            author_type: 'agent',
            author_id: message.agent_id,
          },
        ];
      }
      case 'message:broadcast_send': {
        const messages = [{ responses: message.message.responses, type: 'broadcast' }];

        return [
          ...acc,
          {
            context: message.context,
            tags: message.tags,
            time,
            channel,
            integration_id,
            messages,
            request_id: message.request_id,
            broadcast_id: message.message?.debug?.broadcast?.broadcast_id,
            broadcast_name: message.message?.debug?.broadcast?.broadcast_name,
            author_type: 'broadcast',
          },
        ];
      }
      case 'message:campaign_send': {
        const messages = [{ responses: message.message.responses, type: 'campaign' }];

        return [
          ...acc,
          {
            context: message.context,
            tags: message.tags,
            time,
            channel,
            integration_id,
            messages,
            request_id: message.request_id,
            campaign_id: message.message?.debug?.campaign?.campaign_id,
            campaign_name: message.message?.debug?.campaign?.campaign_name,
            author_type: 'campaign',
          },
        ];
      }

      default:
        return acc;
    }
  }, []);
  return newNodes;
};

const findUserMessage = (node: NodeType): LegacyUserMessage | undefined =>
  node.messages?.find((m) => m.type === 'user') as LegacyUserMessage | undefined;

const findBrainMessage = (node: NodeType): LegacyBrainMessage | undefined =>
  node.messages?.find((m) => m.type === 'brain') as LegacyBrainMessage | undefined;

const findAgentMessages = (node: NodeType): AgentMessage[] =>
  node.messages?.filter((m) => m.type === 'agent') as AgentMessage[];

const collectResponses = (node: NodeType): ResponseMessage[] => {
  const brain = findBrainMessage(node);
  if (brain) {
    return brain.responses ?? [];
  }
  const first = node.messages?.[0];
  if (first && (first.type === 'broadcast' || first.type === 'campaign')) {
    return (first as { responses?: ResponseMessage[] }).responses ?? [];
  }
  return [];
};

const hasInterruptedResponse = (responses: ResponseMessage[]): boolean =>
  responses.some((r) => 'error_code' in r && Boolean(r.error_code));

const inferCategoryKindFromNode = (node: NodeType): ConversationTurnCategoryKind => {
  // `author_type` is typed strictly but `calculateSessionNodes` assigns
  // 'broadcast' / 'campaign' at runtime — compare via string to allow both.
  const authorType = node.author_type as string | undefined;
  if (authorType === 'broadcast') {
    return 'broadcast';
  }
  if (authorType === 'campaign') {
    return 'campaign';
  }
  if (node.author_type === 'agent') {
    return 'agent';
  }
  // AI Response (Trigger LLM) action — route to the workflow variant so the
  // Details panel renders its deep-link row even when no node name is present.
  if (node.steps?.some((step) => step.type === DebugStepType.AI_RESPONSE)) {
    return 'workflow';
  }
  if (node.nodes_stack && node.nodes_stack.length > 0 && node.name) {
    return 'workflow';
  }
  if (isCollectionMessage(node.collection)) {
    return 'knowledge_base';
  }
  if (node.standalone_question || node.planner_intents?.length) {
    return 'guidelines_llm';
  }
  if (node.tool_calls?.tool_invocations?.length) {
    return 'mcp';
  }
  if (node.author_type === 'user') {
    return 'no_details';
  }
  return 'unknown';
};

const parseTime = (time: string | undefined): string | undefined =>
  parseTimestamp(time ?? '')?.toISOString();

const buildRequest = (
  node: NodeType,
  userMessage: LegacyUserMessage | undefined
): ConversationTurn['request'] => {
  if (!userMessage) {
    return null;
  }
  return {
    text: userMessage.text ?? '',
    isAudio: Boolean(userMessage.isAudio),
    attachments: userMessage.attachments ?? [],
    intents: userMessage.intents ?? undefined,
    entities: userMessage.entities,
    isFlowResponse: Boolean(userMessage.isFlowResponse),
  };
};

const buildMetadata = (
  node: NodeType,
  userMessage: LegacyUserMessage | undefined,
  responses: ResponseMessage[]
): ConversationTurn['metadata'] => {
  const triggeredNode = node.nodes_stack?.[(node.nodes_stack?.length ?? 0) - 1];
  const intentFromNode = node.intents?.[0];
  const intentFromMessage = userMessage?.intents?.[0];
  const intent = intentFromNode ?? intentFromMessage ?? undefined;
  const messagesHasError = node.messages?.some((m) => m.type === 'error');
  const agentMessages = findAgentMessages(node);

  return {
    intent: intent ?? undefined,
    tags: node.tags,
    triggeredNode:
      triggeredNode && triggeredNode.name
        ? { id: triggeredNode.node_id, name: triggeredNode.name }
        : undefined,
    knowledgeBase: node.collection?.collection_id
      ? {
          id: node.collection.collection_id,
          name: node.name ?? '',
          contents: node.collection.fragments ?? [],
        }
      : undefined,
    agentMessages: agentMessages.length ? agentMessages : undefined,
    broadcast: node.broadcast_id
      ? { id: node.broadcast_id, name: node.broadcast_name ?? undefined }
      : undefined,
    campaign: node.campaign_id
      ? { id: node.campaign_id, name: node.campaign_name ?? undefined }
      : undefined,
    toolCalls: node.tool_calls?.tool_invocations,
    nodesStack: node.nodes_stack,
    context: node.context,
    steps: node.steps,
    authentication: node.authentication,
    collection: node.collection,
    plannerIntents: node.planner_intents,
    standaloneQuestion: node.standalone_question,
    isError: Boolean(messagesHasError),
    isInterrupted: hasInterruptedResponse(responses),
    isAuthentication: Boolean(node.authentication),
    // Snapshot the original NodeType for lossless replay rendering. See the
    // `TurnMetadata.legacyNode` JSDoc — only the replay parser sets this.
    legacyNode: node,
  };
};

/**
 * Maps a single legacy `NodeType` to the canonical `ConversationTurn` shape.
 * Pure — same input → same output, no React or Redux dependencies.
 */
export const nodeToLegacyTurn = (node: NodeType, index = 0): ConversationTurn => {
  const userMessage = findUserMessage(node);
  const responses = collectResponses(node);
  const kind = inferCategoryKindFromNode(node);
  const id = node.id ?? node.request_id ?? node.key ?? `turn-${index}`;

  return {
    id,
    at: parseTime(node.time),
    request: buildRequest(node, userMessage),
    category: buildCategory(kind),
    responses,
    metadata: buildMetadata(node, userMessage, responses),
    reaction: node.reaction ?? undefined,
    corrections: node.corrections,
    requestId: node.request_id ?? userMessage?.request_id,
  };
};

/**
 * Replay-path parser. Turns the raw `LogMessage[]` from the analytics GraphQL
 * into `ConversationTurn[]` for the new modal.
 */
export const messagesToTurns = (messages?: LogMessage[]): ConversationTurn[] => {
  const nodes = calculateSessionNodes(messages);
  return nodes.map((node, index) => nodeToLegacyTurn(node, index));
};

// ─────────────────────────────────────────────────────────────────────────────
// Legacy boundary: ConversationTurn → NodeType (consumed only by LegacyChatBody)
// ─────────────────────────────────────────────────────────────────────────────

const buildLegacyAuthorType = (
  kind: ConversationTurn['category']['kind']
): NodeType['author_type'] => {
  switch (kind) {
    case 'agent':
      return 'agent';
    case 'broadcast':
    case 'campaign':
    case 'voice_campaign':
      return 'brain';
    case 'no_details':
      return 'user';
    default:
      return 'brain';
  }
};

const buildLegacyMessages = (turn: ConversationTurn): Message[] => {
  if (turn.category.kind === 'agent' && turn.metadata.agentMessages?.length) {
    return turn.metadata.agentMessages;
  }

  const messages: Message[] = [];

  if (turn.request) {
    const userMsg: UserMessage = {
      type: 'user',
      text: turn.request.text,
      intents: turn.request.intents ?? null,
      entities: turn.request.entities,
      isAudio: turn.request.isAudio,
      attachments: turn.request.attachments,
      request_id: turn.requestId,
    };
    messages.push(userMsg);
  }

  if (turn.responses.length > 0) {
    const brainMsg: BrainMessage = {
      type: 'brain',
      responses: turn.responses as ResponseMessage[],
    };
    messages.push(brainMsg);
  }

  return messages;
};

/**
 * **The single legacy boundary.** Maps a `ConversationTurn` back to the
 * `NodeType` shape that the legacy `Messages` component and its descendants
 * still consume. Deleting this function is the signal that the legacy chat
 * body has been rewritten.
 *
 * Replay turns carry the original `NodeType` in `metadata.legacyNode` — we
 * return it directly so multi-message user turns (voice interruptions),
 * `author_id`, `debugLogs`, and any other field that the lossy
 * `TurnRequest`/`responses` round-trip would drop are preserved for the
 * legacy renderers. Test-mode turns don't have a snapshot, so we synthesize.
 */
export const turnToLegacyNode = (turn: ConversationTurn): NodeType => {
  if (turn.metadata.legacyNode) {
    return turn.metadata.legacyNode;
  }

  const nodesStack = turn.metadata.nodesStack ?? [];
  const lastStack = nodesStack[nodesStack.length - 1];
  const toolInvocations = turn.metadata.toolCalls;

  return {
    id: turn.id,
    key: turn.id,
    name: lastStack?.name ?? null,
    nodes_stack: nodesStack.length > 0 ? nodesStack : undefined,
    author_type: buildLegacyAuthorType(turn.category.kind),
    time: turn.at,
    context: turn.metadata.context,
    debugLogs: undefined,
    steps: turn.metadata.steps,
    collection: turn.metadata.collection,
    request_id: turn.requestId,
    intents: turn.request?.intents,
    planner_intents: turn.metadata.plannerIntents,
    standalone_question: turn.metadata.standaloneQuestion,
    broadcast_id: turn.metadata.broadcast?.id,
    broadcast_name: turn.metadata.broadcast?.name,
    campaign_id: turn.metadata.campaign?.id,
    campaign_name: turn.metadata.campaign?.name,
    corrections: turn.corrections,
    reaction: turn.reaction ?? null,
    tool_calls: toolInvocations?.length ? { tool_invocations: toolInvocations } : undefined,
    authentication: turn.metadata.authentication,
    messages: buildLegacyMessages(turn),
  };
};
